import React, {useEffect, useMemo, useRef, useState} from 'react';
import Dialog from '../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../components/dialogs/DialogContent';
import SingleSelectField from "../../../../components/fields/select/SingleSelectField";
import TextField from '../../../../components/fields/textfield/TextField';
import Grid from '../../../../components/dialogs/Grid';
import {t} from '../../../../store/localization/TranslMessasge';
import {ConstraintDataPluginRegistry} from "./constraint/ConstraintDataPluginRegistry";
import {DataTypeTranslationKey} from './DataTypeTranslationKey';
import {CommonTranslationKey} from '../CommonTranslationKey';
import DataTypeConstraintsGrid from './constraint/DataTypeConstraintsGrid';
import SectionTitle from '../../../../components/section/SectionTitle';
import {DataTypeApiClient, DataTypeErrorCode} from '../../../../common/apis/datatype/DataTypeApiClient';
import {DataTypeConstraintCreateDialog} from "./constraint/DataTypeConstraintCreateDialog";
import {
    DataTypeConstraintCreateDto,
    DataTypeConstraintDto
} from "../../../../common/apis/datatype/DataTypeConstraintDto";
import {CoreDataType, DataTypeDefinitionCreateDto} from '../../../../common/apis/datatype/DataTypeDefinitionDto';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../components/button/ButtonLayout";
import {Button} from "../../../../components/button/Button";
import {CancelButton} from "../../../../components/button/CancelButton";
import DialogActions from "../../../../components/dialogs/DialogActions";
import Snackbar from "../snackbar/Snackbar";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {ValidationError} from "../../../../common/ValidationError";
import { CoreDataTypeFormatter } from './CoreDataTypeFormatter';

const DEFAULT_DATA_TYPE = CoreDataType.STRING;

interface DataTypeCreateDialogProps {
    open: boolean;
    onCreated: (dataTypeDefinitionId: string) => void;
    onClose: () => void;
}

export function DataTypeCreateDialog({ open, onCreated, onClose }: Readonly<DataTypeCreateDialogProps>) {
    const dataTypeApiClient = useRef(new DataTypeApiClient());

    const [dataType, setDataType] = useState<Partial<DataTypeDefinitionCreateDto>>({});
    const [constraints, setConstraints] = useState<DataTypeConstraintDto[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [constraintsVisible, setConstraintsVisible] = useState(true);
    const [constraintCreateDialogOpen, setConstraintCreateDialogOpen] = useState(false);
    const [inProgress, setInProgress] = useState(false);

    const coreDataTypes = useMemo(() => {
        const types = Object.values(CoreDataType);
        types.sort((a, b) => CoreDataTypeFormatter.formatCoreDataType(a, t).localeCompare(CoreDataTypeFormatter.formatCoreDataType(b, t)));
        return types;
    }, []);

    useEffect(() => {
        if (open) {

            setDataType({
                name: '',
                coreDataType: DEFAULT_DATA_TYPE,
                constraints: []
            });
            setErrors({});
            setConstraints([]);
            setConstraintsVisible(true);
        }
    }, [open]);

    function changeCoreDataType(newCoreDataType: CoreDataType | null) {
        setDataType({...dataType, coreDataType: newCoreDataType ?? undefined});
        resetConstraints(newCoreDataType);
    }

    function resetConstraints(coreDataType: CoreDataType | null) {
        setConstraints([]);
        if (coreDataType) {
            const availableTypes = ConstraintDataPluginRegistry.getAvailableTypesByDataType(coreDataType);
            setConstraintsVisible(availableTypes.length > 0);
        } else {
            setConstraintsVisible(false);
        }
    }

    function openConstraintCreateDialog() {
        setConstraintCreateDialogOpen(true);
    }

    function closeConstraintCreateDialog() {
        setConstraintCreateDialogOpen(false);
    }

    async function addConstraint(constraint: DataTypeConstraintCreateDto): Promise<void> {
        return new Promise((resolve, reject) => {
            if (constraints.some(c => c.name === constraint.name?.trim())) {
                reject(new ValidationError({code: DataTypeErrorCode.CONSTRAINTS_CONFLICTING_NAME}));
            } else {
                setConstraints([...constraints, {
                    ...constraint,
                    id: crypto.randomUUID(),
                    acl: {canDelete: true, canUpdate: true}
                }]);
                resolve();
            }
        });
    }

    function removeConstraint(constraintIdToRemove: string) {
        setConstraints(constraints.filter(constraint => constraint.id !== constraintIdToRemove));
    }

    function createDataType() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const dataTypeCreate: DataTypeDefinitionCreateDto = {
            name: dataType.name!.trim(),
            description: dataType.description?.trim(),
            coreDataType: dataType.coreDataType!,
            constraints: constraints.map(constraint => ({
                name: constraint.name,
                description: constraint.description,
                constraintData: constraint.constraintData
            }))
        };

        dataTypeApiClient.current.create(dataTypeCreate)
            .then((definitionId) => {
                onCreated(definitionId);
                onClose();
                Snackbar.success(t(DataTypeTranslationKey.DATA_TYPE_CREATED_SUCCESSFULLY));
            })
            .catch((error) => {
                if (error instanceof ValidationError && error.error.code === DataTypeErrorCode.CONSTRAINTS_CONFLICTING_NAME) {
                    setErrors({constraints: t(DataTypeTranslationKey.CONSTRAINTS_NAMES_MUST_BE_UNIQUE)});
                } else {
                    Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                }
            })
            .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!dataType.name?.trim()) {
            newErrors.name = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!dataType.coreDataType) {
            newErrors.coreDataType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={t(DataTypeTranslationKey.NEW_DATA_TYPE)}
                        onDialogClosed={onClose} />
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(DataTypeTranslationKey.NAME)}
                                    value={dataType.name}
                                    onChange={value => setDataType({...dataType, name: value})}
                                    errorMessage={errors.name}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(CommonTranslationKey.DESCRIPTION)}
                                    value={dataType.description ?? ''}
                                    onChange={value => setDataType({...dataType, description: value})}
                                    multiline
                                    rows={4}/>
                        </Grid>
                        <Grid item xs={12}>
                            <SingleSelectField id={"core-data-type"}
                                    label={t(DataTypeTranslationKey.CORE_DATA_TYPE)}
                                    value={dataType.coreDataType}
                                    onChange={value => changeCoreDataType(value)}
                                    options={coreDataTypes}
                                    getOptionLabel={(option) => CoreDataTypeFormatter.formatCoreDataType(option, t)}
                                    errorMessage={errors.coreDataType}
                                    required={true}/>
                        </Grid>
                        {constraintsVisible &&
                                <>
                                    <Grid item xs={12}>
                                        <SectionTitle>{t(DataTypeTranslationKey.CONSTRAINTS)}</SectionTitle>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DataTypeConstraintsGrid
                                                constraints={constraints}
                                                createEnabled={true}
                                                onCreateClicked={openConstraintCreateDialog}
                                                onDeleteClicked={removeConstraint}/>
                                    </Grid>
                                </>
                        }
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <Button label={t(CommonTranslationKey.CREATE)}
                                    variant="contained"
                                    onClick={createDataType}
                                    disabled={inProgress} />
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress} />
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>

            <DataTypeConstraintCreateDialog open={constraintCreateDialogOpen}
                    coreDataType={dataType.coreDataType!}
                    onCreateConstraint={addConstraint}
                    onClose={closeConstraintCreateDialog}
            />
        </>
    );
}
